<template>
  <div class="mx-auto w-full max-w-[1380px] px-4 py-6 font-inter text-foreground">
    <section class="mb-4">
      <div class="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">Mining Directory</p>
          <h1 class="text-3xl font-bold text-gray-900">{{ pageTitle }}</h1>
        </div>
        <p
          v-if="displayDirectoryTotal !== null"
          class="inline-flex h-fit rounded-full bg-[#f5f5f5] px-3 py-1 text-xs font-semibold text-[#737373]"
        >
          {{ displayDirectoryTotal.toLocaleString() }} {{ activeMode === 'project' ? 'projects' : 'companies' }}
        </p>
      </div>

      <p class="mt-1 max-w-3xl text-sm text-gray-500">
        {{ pageSubtitle }}
      </p>
    </section>

    <section class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div class="grid gap-3 md:grid-cols-[auto,minmax(0,1fr),auto]">
        <div class="flex items-end">
          <div class="inline-flex overflow-hidden rounded-2xl border border-slate-300 bg-gradient-to-br from-slate-100 via-white to-slate-50 p-1.5 shadow-sm ring-1 ring-slate-200/70">
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-sky-200"
              :class="activeMode === 'company'
                ? 'border-sky-200 bg-sky-600 text-white shadow-md shadow-sky-100'
                : 'border-transparent bg-white/70 text-slate-600 hover:border-slate-300 hover:bg-white hover:text-slate-900'"
              @click="setMode('company')"
            >
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M3 21h18" />
                <path d="M5 21V7l7-4 7 4v14" />
                <path d="M9 10h.01" />
                <path d="M9 14h.01" />
                <path d="M15 10h.01" />
                <path d="M15 14h.01" />
              </svg>
              Search Companies
              <span class="rounded-full px-2 py-0.5 text-[11px] font-bold"
                :class="activeMode === 'company' ? 'bg-white/20 text-white ring-1 ring-white/25' : 'bg-slate-200 text-slate-700'">
                {{ companyDirectoryTotal.toLocaleString() }}
              </span>
            </button>
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-amber-200"
              :class="activeMode === 'project'
                ? 'border-amber-200 bg-amber-500 text-white shadow-md shadow-amber-100'
                : 'border-transparent bg-white/70 text-slate-600 hover:border-slate-300 hover:bg-white hover:text-slate-900'"
              @click="setMode('project')"
            >
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M4 6h9v14H4z" />
                <path d="M13 10h7v10h-7z" />
                <path d="M8 10h.01" />
                <path d="M16.5 14h.01" />
              </svg>
              Search Projects
              <span class="rounded-full px-2 py-0.5 text-[11px] font-bold"
                :class="activeMode === 'project' ? 'bg-white/20 text-white ring-1 ring-white/25' : 'bg-slate-200 text-slate-700'">
                {{ projectDirectoryTotal.toLocaleString() }}
              </span>
            </button>
          </div>
        </div>

        <div>
          <label class="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Search</label>
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="searchPlaceholder"
            class="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none placeholder:text-slate-400 transition focus:border-newPrimary focus:ring-2 focus:ring-newPrimary/20"
          />
        </div>

        <div class="flex items-end gap-2">
          <label
            class="inline-flex h-11 cursor-pointer items-center gap-2 rounded-lg border px-4 text-sm font-semibold transition"
            :class="bookmarkedOnly
              ? 'border-newPrimary bg-newPrimary text-white shadow-sm'
              : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'"
          >
            <input
              :checked="bookmarkedOnly"
              type="checkbox"
              class="sr-only"
              @change="handleBookmarkedOnlyChange"
            />
            <Bookmark class="h-4 w-4" :class="{ 'fill-current': bookmarkedOnly }" />
            Bookmarked only
          </label>
          <button
            class="h-11 rounded-lg border border-slate-300 px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            @click="clearFilters"
          >
            Reset all filters
          </button>
        </div>
      </div>
    </section>

    <section
      v-if="isAccessLoading"
      class="mt-4 flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-sm text-muted-foreground"
    >
      <span class="h-4 w-4 animate-spin rounded-full border-2 border-newPrimary/30 border-t-newPrimary"></span>
      Checking your directory access...
    </section>
    
    <section v-if="!isAllowed && !isAccessLoading" class="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-5 text-amber-900">
      Mining company search is available for contractors registered in the mining or energy industry.
    </section>

    <template v-else>
      <section class="mt-5 grid gap-5 xl:grid-cols-[300px,minmax(0,1fr)]">
        <aside class="h-fit rounded-2xl border border-slate-200 bg-white shadow-sm xl:sticky xl:top-4">
          <div class="flex items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
            <h2 class="text-sm font-semibold uppercase tracking-wide text-slate-800">Filters</h2>
            <span
              v-if="activeFilterCount > 0"
              class="inline-flex items-center rounded-full bg-[#f5f5f5] px-2.5 py-1 text-[11px] font-semibold text-[#737373]"
            >
              {{ activeFilterCount }}
            </span>
          </div>

          <div class="max-h-[calc(100vh-8rem)] space-y-5 overflow-auto px-4 py-4">
            <div class="space-y-2">
              <label class="block text-xs font-semibold uppercase tracking-wide text-slate-500">Sort Results</label>
              <select
                v-model="sort"
                class="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-newPrimary focus:ring-2 focus:ring-newPrimary/20"
              >
                <option
                  v-for="option in sortOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>

            <div class="space-y-2">
              <p class="block text-xs font-semibold uppercase tracking-wide text-slate-500">Industry</p>
              <div class="space-y-1.5">
                <label
                  v-for="industry in INDUSTRY_FILTERS"
                  :key="industry.value"
                  class="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50"
                >
                  <input
                    :checked="selectedIndustries.includes(industry.value)"
                    type="checkbox"
                    class="h-4 w-4 rounded border-slate-300 text-newPrimary focus:ring-newPrimary/30"
                    @change="toggleIndustry(industry.value)"
                  />
                  <span>{{ industry.label }}</span>
                </label>
              </div>
            </div>

            <template v-if="activeMode === 'company'">
              <div v-if="SHOW_SUBCATEGORY_FILTER && companySubcategoryOptions.length" class="space-y-2">
                <p class="block text-xs font-semibold uppercase tracking-wide text-slate-500">Subcategory</p>
                <div class="space-y-1.5">
                  <label
                    v-for="subcategory in companySubcategoryOptions"
                    :key="subcategory.slug || subcategory.name"
                    class="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50"
                  >
                    <input
                      :checked="selectedIndustrySubcategories.includes(subcategory.slug)"
                      type="checkbox"
                      class="h-4 w-4 rounded border-slate-300 text-newPrimary focus:ring-newPrimary/30"
                      @change="toggleIndustrySubcategory(subcategory.slug)"
                    />
                    <span>{{ subcategory.name }}</span>
                  </label>
                </div>
              </div>

              <div class="space-y-2">
                <p class="block text-xs font-semibold uppercase tracking-wide text-slate-500">Company Commodities</p>
                <input
                  v-model="commoditySearchQuery"
                  type="text"
                  placeholder="Search commodities..."
                  class="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-newPrimary focus:ring-2 focus:ring-newPrimary/20"
                />
                <div v-if="selectedCommodities.length > 0" class="flex flex-wrap gap-2">
                  <button
                    v-for="commodity in selectedCommodities"
                    :key="commodity"
                    type="button"
                    class="rounded-full border border-newPrimary bg-newPrimary px-3 py-1 text-xs font-semibold text-white transition hover:bg-newPrimary/90"
                    @click="toggleCommodity(commodity)"
                  >
                    {{ commodity }}
                  </button>
                </div>
                <div v-if="commoditySearchQuery.trim()" class="flex flex-wrap gap-2">
                  <button
                    v-for="commodity in filteredCommodityOptions"
                    :key="commodity"
                    type="button"
                    class="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-newPrimary hover:text-slate-900"
                    @click="toggleCommodity(commodity)"
                  >
                    {{ commodity }}
                  </button>
                </div>
                <p v-if="commoditySearchQuery.trim() && filteredCommodityOptions.length === 0" class="text-xs text-slate-500">
                  No active commodities match your search.
                </p>
              </div>

              <div class="space-y-2">
                <p class="block text-xs font-semibold uppercase tracking-wide text-slate-500">Head Office State</p>
                <div class="space-y-1.5">
                  <label
                    v-for="state in stateOptions"
                    :key="`company-${state.value}`"
                    class="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50"
                  >
                    <input
                      :checked="selectedStates.includes(state.value)"
                      type="checkbox"
                      class="h-4 w-4 rounded border-slate-300 text-newPrimary focus:ring-newPrimary/30"
                      @change="toggleState(state.value)"
                    />
                    <span>{{ state.label }}</span>
                  </label>
                </div>
              </div>

              <div class="space-y-2">
                <p class="block text-xs font-semibold uppercase tracking-wide text-slate-500">Cash in Bank (AUD)</p>
                <div class="space-y-1.5">
                  <label
                    v-for="option in CASH_BANDS.filter((item) => item.value !== '')"
                    :key="option.value"
                    class="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50"
                  >
                    <input
                      :checked="selectedCashBands.includes(option.value)"
                      type="checkbox"
                      class="h-4 w-4 rounded border-slate-300 text-newPrimary focus:ring-newPrimary/30"
                      @change="toggleCashBand(option.value)"
                    />
                    <span>{{ option.label }}</span>
                  </label>
                </div>
              </div>

              <div class="space-y-2">
                <p class="block text-xs font-semibold uppercase tracking-wide text-slate-500">Employee Size</p>
                <div class="space-y-1.5">
                  <label
                    v-for="size in EMPLOYEE_SIZE_FILTERS"
                    :key="size"
                    class="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50"
                  >
                    <input
                      :checked="selectedEmployeeSizes.includes(size)"
                      type="checkbox"
                      class="h-4 w-4 rounded border-slate-300 text-newPrimary focus:ring-newPrimary/30"
                      @change="toggleEmployeeSize(size)"
                    />
                    <span>{{ size }}</span>
                  </label>
                </div>
              </div>

              <div class="space-y-2">
                <p class="block text-xs font-semibold uppercase tracking-wide text-slate-500">Hiring Activity</p>
                <label
                  class="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50"
                >
                  <input
                    v-model="hasActiveJobs"
                    type="checkbox"
                    class="h-4 w-4 rounded border-slate-300 text-newPrimary focus:ring-newPrimary/30"
                  />
                  <span>Companies with active jobs</span>
                </label>
              </div>
            </template>

            <template v-else>
              <div class="space-y-2">
                <p class="block text-xs font-semibold uppercase tracking-wide text-slate-500">Project Location</p>
                <div class="space-y-1.5">
                  <label
                    v-for="state in stateOptions"
                    :key="`project-${state.value}`"
                    class="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50"
                  >
                    <input
                      :checked="selectedProjectStates.includes(state.value)"
                      type="checkbox"
                      class="h-4 w-4 rounded border-slate-300 text-newPrimary focus:ring-newPrimary/30"
                      @change="toggleProjectState(state.value)"
                    />
                    <span>{{ state.label }}</span>
                  </label>
                </div>
              </div>

              <div class="space-y-2">
                <p class="block text-xs font-semibold uppercase tracking-wide text-slate-500">Project Commodities</p>
                <input
                  v-model="commoditySearchQuery"
                  type="text"
                  placeholder="Search commodities..."
                  class="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-newPrimary focus:ring-2 focus:ring-newPrimary/20"
                />
                <div v-if="selectedCommodities.length > 0" class="flex flex-wrap gap-2">
                  <button
                    v-for="commodity in selectedCommodities"
                    :key="commodity"
                    type="button"
                    class="rounded-full border border-newPrimary bg-newPrimary px-3 py-1 text-xs font-semibold text-white transition hover:bg-newPrimary/90"
                    @click="toggleCommodity(commodity)"
                  >
                    {{ commodity }}
                  </button>
                </div>
                <div v-if="commoditySearchQuery.trim()" class="flex flex-wrap gap-2">
                  <button
                    v-for="commodity in filteredCommodityOptions"
                    :key="commodity"
                    type="button"
                    class="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-newPrimary hover:text-slate-900"
                    @click="toggleCommodity(commodity)"
                  >
                    {{ commodity }}
                  </button>
                </div>
                <p v-if="commoditySearchQuery.trim() && filteredCommodityOptions.length === 0" class="text-xs text-slate-500">
                  No active commodities match your search.
                </p>
              </div>

              <div class="space-y-2">
                <p class="block text-xs font-semibold uppercase tracking-wide text-slate-500">Project Stage</p>
                <div class="space-y-1.5">
                  <label
                    v-for="stage in PROJECT_STAGES"
                    :key="stage"
                    class="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50"
                  >
                    <input
                      :checked="selectedProjectStages.includes(stage)"
                      type="checkbox"
                      class="h-4 w-4 rounded border-slate-300 text-newPrimary focus:ring-newPrimary/30"
                      @change="toggleProjectStage(stage)"
                    />
                    <span>{{ stage }}</span>
                  </label>
                </div>
              </div>
            </template>
          </div>
        </aside>

        <div class="grid gap-4 xl:grid-cols-[minmax(0,1fr),440px]">
          <div class="min-w-0">
            <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
              <p class="text-sm font-semibold text-slate-700">
                {{ resultsSummary }}
              </p>
              <p class="text-xs font-medium text-slate-500">Page {{ page }} of {{ totalPages }}</p>
            </div>

            <div v-if="isLoading" class="space-y-3">
              <div class="flex items-center justify-center rounded-xl border border-slate-200 bg-white py-5">
                <span class="h-5 w-5 animate-spin rounded-full border-2 border-newPrimary/30 border-t-newPrimary"></span>
              </div>
              <div v-for="n in 4" :key="n" class="h-24 animate-pulse rounded-xl border border-slate-200 bg-slate-50"></div>
            </div>

            <div v-else-if="bookmarkedOnly && companyItems.length === 0" class="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500">
              You haven't bookmarked any companies yet. Bookmark companies from the directory to see them here.
            </div>

            <div v-else-if="activeMode === 'company' && companyItems.length === 0" class="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500">
              No mining companies match your search yet.
            </div>

            <div v-else-if="activeMode === 'project' && projectItems.length === 0" class="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500">
              No mining projects match your search yet.
            </div>

            <transition-group
              v-else-if="activeMode === 'company'"
              name="company-list"
              tag="div"
              class="space-y-3"
            >
              <article
                v-for="company in companyItems"
                :key="company.slug || `${company.id}-${company.name}`"
                class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-slate-300 hover:shadow-md"
                @mouseenter="selectedResultId = getCompanyResultId(company)"
                @mouseleave="selectedResultId = null"
              >
                <div class="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                  <div class="min-w-0 flex-1">
                    <div class="flex items-start gap-3">
                      <div
                        v-if="company.logo"
                        class="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white p-2 shadow-sm"
                      >
                        <img
                          :src="company.logo"
                          :alt="company.name"
                          class="max-h-8 max-w-full object-contain"
                        />
                      </div>
                      <div class="min-w-0 flex-1">
                        <div class="flex flex-wrap items-center gap-2">
                          <router-link
                            v-if="company.slug"
                            :to="{ name: 'ContractorCompanyPage', params: { slug: company.slug }, query: route.query }"
                            class="truncate text-base font-semibold leading-tight text-slate-900 hover:text-newPrimary"
                          >
                            {{ company.name }}
                          </router-link>
                          <span
                            v-else
                            class="truncate text-base font-semibold leading-tight text-slate-900"
                          >
                            {{ company.name }}
                          </span>
                          <span
                            v-if="company.is_private_company"
                            class="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600"
                          >
                            Private
                          </span>
                          <span
                            v-if="company.industry_subcategory"
                            class="inline-flex rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700 ring-1 ring-sky-100"
                          >
                            {{ company.industry_subcategory }}
                          </span>
                          <button
                            v-if="company.id"
                            type="button"
                            class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition focus:outline-none focus:ring-2 focus:ring-newPrimary/20 disabled:cursor-not-allowed disabled:opacity-60"
                            :class="company.is_bookmarked
                              ? 'border-newPrimary bg-newPrimary text-white hover:bg-newPrimary/90'
                              : 'border-slate-300 bg-white text-slate-500 hover:border-newPrimary hover:text-newPrimary'"
                            :disabled="bookmarkSavingIds.has(company.id)"
                            :aria-label="company.is_bookmarked ? `Remove ${company.name} bookmark` : `Bookmark ${company.name}`"
                            :title="company.is_bookmarked ? 'Remove bookmark' : 'Bookmark company'"
                            @click.stop="toggleCompanyBookmark(company)"
                          >
                            <Bookmark class="h-4 w-4" :class="{ 'fill-current': company.is_bookmarked }" />
                          </button>
                        </div>

                        <div class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                          <span
                            v-if="company.office_address"
                            class="max-w-full truncate sm:max-w-[320px]"
                            :title="company.office_address"
                          >
                            {{ company.office_address }}
                          </span>
                          <a
                            v-if="company.website"
                            :href="normalizeWebsite(company.website)"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="font-semibold text-newPrimary hover:underline"
                          >
                            {{ stripProtocol(company.website) }}
                          </a>
                        </div>

                        <p v-if="company.summary" class="mt-2 line-clamp-1 text-sm leading-relaxed text-slate-600">
                          {{ company.summary }}
                        </p>
                      </div>
                    </div>

                    <div class="mt-3 flex flex-wrap items-center gap-2">
                      <span
                        v-for="commodity in visibleCommodities(company.commodities)"
                        :key="commodity"
                        class="rounded-full bg-sky-50 px-2.5 py-1 text-[11px] font-semibold text-sky-700 ring-1 ring-sky-100"
                      >
                        {{ commodity }}
                      </span>
                      <span
                        v-if="company.commodities.length > 3"
                        class="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600"
                      >
                        +{{ company.commodities.length - 3 }} more
                      </span>
                    </div>
                  </div>

                  <div class="grid min-w-0 shrink-0 grid-cols-2 gap-3 xl:w-[220px] xl:grid-cols-1 xl:text-right">
                    <div>
                      <p class="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Market cap</p>
                      <p v-if="company.is_private_company" class="mt-1 text-sm font-semibold text-slate-700">Private</p>
                      <p v-else class="mt-1 text-sm font-semibold text-slate-900">{{ formatMarketCapValue(company.market_cap) }}</p>
                    </div>
                    <div v-if="activeCompanySortMetric">
                      <p class="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{{ activeCompanySortMetric.label }}</p>
                      <p class="mt-1 text-sm font-semibold text-slate-900">
                        {{ formatActiveCompanySortValue(company) }}
                      </p>
                    </div>
                  </div>
                </div>
              </article>
            </transition-group>

            <transition-group
              v-else
              name="company-list"
              tag="div"
              class="space-y-3"
            >
              <article
                v-for="project in projectItems"
                :key="project.id"
                class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-slate-300 hover:shadow-md"
                @mouseenter="selectedResultId = project.id"
                @mouseleave="selectedResultId = null"
              >
                <div class="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                  <div class="min-w-0 flex-1">
                    <div class="flex flex-wrap items-center gap-2">
                      <h2 class="truncate text-base font-semibold leading-tight text-slate-900">
                        {{ project.name || `${project.company_name} project` }}
                      </h2>
                      <span
                        v-if="project.stage"
                        class="inline-flex rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700 ring-1 ring-amber-100"
                      >
                        {{ project.stage }}
                      </span>
                    </div>

                    <div class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                      <router-link
                        v-if="project.company_slug"
                        :to="{ name: 'ContractorCompanyPage', params: { slug: project.company_slug }, query: route.query }"
                        class="font-semibold text-newPrimary hover:underline"
                      >
                        {{ project.company_name }}
                      </router-link>
                      <span v-else>{{ project.company_name }}</span>
                      <span v-if="project.location_label">{{ project.location_label }}</span>
                    </div>

                    <p v-if="project.description" class="mt-2 line-clamp-2 text-sm leading-relaxed text-slate-600">
                      {{ project.description }}
                    </p>

                    <div class="mt-3 flex flex-wrap items-center gap-2">
                      <span
                        v-for="commodity in visibleCommodities(project.commodities)"
                        :key="commodity"
                        class="rounded-full bg-sky-50 px-2.5 py-1 text-[11px] font-semibold text-sky-700 ring-1 ring-sky-100"
                      >
                        {{ commodity }}
                      </span>
                      <span
                        v-if="project.commodities.length > 3"
                        class="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600"
                      >
                        +{{ project.commodities.length - 3 }} more
                      </span>
                    </div>

                    <div v-if="project.activity_feed?.length" class="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
                      <p class="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Latest update
                        <span v-if="project.activity_feed[0].date">
                          · {{ formatDateAsDayMonthYear(project.activity_feed[0].date) }}
                        </span>
                      </p>
                      <p class="mt-1 line-clamp-2">{{ project.activity_feed[0].text }}</p>
                    </div>
                  </div>

                  <div class="grid shrink-0 grid-cols-1 gap-3 xl:min-w-[220px] xl:text-right">
                    <div>
                      <p class="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Project location</p>
                      <p class="mt-1 text-sm font-semibold text-slate-900">{{ project.location_label || 'Not provided' }}</p>
                    </div>
                  </div>
                </div>
              </article>
            </transition-group>

            <div v-if="totalPages > 1" class="mt-4 flex items-center justify-center gap-2">
              <button
                class="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="page <= 1 || isLoading"
                @click="runSearch(page - 1)"
              >
                Previous
              </button>
              <span class="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white">{{ page }}</span>
              <button
                class="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="page >= totalPages || isLoading"
                @click="runSearch(page + 1)"
              >
                Next
              </button>
            </div>
          </div>

          <aside class="hidden h-fit xl:block xl:sticky xl:top-4">
            <CompanyDirectoryMap
              :mode="activeMode"
              :companies="mappableCompanies"
              :projects="projectItems"
              :selected-id="selectedResultId"
              @marker-click="handleMarkerClick"
            />
          </aside>
        </div>
      </section>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Bookmark } from 'lucide-vue-next'
import { COMMODITIES, EMPLOYEE_SIZES, PROJECT_STAGES } from '@/constants/lookups'
import { formatMarketCap } from '@/composables/useCompanyMarketData'
import { formatDateAsDayMonthYear } from '@/utils/formatters'
import { getCompanyIndustryOptions } from '@/api/shared/industries'
import type { CompanyIndustryOption, CompanyIndustrySubcategory } from '@/types/company'
import CompanyDirectoryMap from '@/components/marketplace/contractor/CompanyDirectoryMap.vue'
import {
  bookmarkContractorCompany,
  getContractorMiningCompanies,
  removeContractorCompanyBookmark,
} from '@/api/contractor/company'

type SearchMode = 'company' | 'project'

type CompanyListItem = {
  id: number | null
  name: string
  slug: string
  logo: string
  website: string
  office_address: string
  summary: string
  market_cap: number | string | null
  cash_in_bank: number | string | null
  cash_runway: number | string | null
  is_private_company: boolean
  is_bookmarked: boolean
  industry_subcategory: string
  industry_subcategory_id?: number | string | null
  commodities: string[]
  office_location_lat?: number | string | null
  office_location_lng?: number | string | null
}

type ProjectListItem = {
  id: string
  name: string
  stage: string
  description: string
  location_label: string
  location_lat: number | string | null
  location_lng: number | string | null
  commodities: string[]
  activity_feed: Array<{ date: string; text: string }>
  company_id: number | string | null
  company_name: string
  company_slug: string
}

type SortOption = {
  value: string
  label: string
}

type CashBandOption = {
  value: string
  label: string
}

const JOB_STATE_FILTERS = [
  { value: 'NSW', label: 'NSW' },
  { value: 'QLD', label: 'QLD' },
  { value: 'WA', label: 'WA' },
  { value: 'SA', label: 'SA' },
  { value: 'VIC', label: 'VIC' },
  { value: 'TAS', label: 'TAS' },
  { value: 'NT', label: 'NT' },
  { value: 'ACT', label: 'ACT' },
  { value: 'Overseas', label: 'Overseas' },
] as const
const EMPLOYEE_SIZE_FILTERS = EMPLOYEE_SIZES
const COMPANY_SORT_OPTIONS: SortOption[] = [
  { value: 'market_cap_desc', label: 'Market Cap: High to low' },
  { value: 'market_cap_asc', label: 'Market Cap: Low to high' },
  { value: 'cash_in_bank_desc', label: 'Cash in Bank: High to low' },
  { value: 'cash_in_bank_asc', label: 'Cash in Bank: Low to high' },
  { value: 'cash_runway_desc', label: 'Cash Runway: High to low' },
  { value: 'cash_runway_asc', label: 'Cash Runway: Low to high' },
  { value: 'name_asc', label: 'Name (A-Z)' },
  { value: 'name_desc', label: 'Name (Z-A)' },
]
const PROJECT_SORT_OPTIONS: SortOption[] = [
  { value: 'name_asc', label: 'Project Name (A-Z)' },
  { value: 'name_desc', label: 'Project Name (Z-A)' },
  { value: 'company_asc', label: 'Company (A-Z)' },
  { value: 'company_desc', label: 'Company (Z-A)' },
  { value: 'stage_asc', label: 'Stage (A-Z)' },
  { value: 'stage_desc', label: 'Stage (Z-A)' },
]
const CASH_BANDS: CashBandOption[] = [
  { value: '', label: 'All ranges' },
  { value: 'lt_3m', label: '< $3m' },
  { value: '3m_8m', label: '$3m to $8m' },
  { value: '8m_20m', label: '$8m to $20m' },
  { value: 'gte_20m', label: '$20m+' },
]
const INDUSTRY_FILTERS = [
  { value: 'mining', label: 'Mining' },
  { value: 'energy', label: 'Energy' },
]
const PER_PAGE = 20

const route = useRoute()
const router = useRouter()

const isAccessLoading = ref(true)
const isAllowed = ref(false)
const isLoading = ref(false)
const hasBootstrapped = ref(false)
const isSyncingFromRoute = ref(false)
const lastLocallySyncedQuery = ref<string | null>(null)
const selectedResultId = ref<string | number | null>(null)

const activeMode = ref<SearchMode>('company')
const searchQuery = ref('')
const commoditySearchQuery = ref('')
const sort = ref('market_cap_desc')
const selectedIndustries = ref<string[]>([])
// Subcategory filter only exposes these subcategories; set SHOW_SUBCATEGORY_FILTER to false to hide it.
const SHOW_SUBCATEGORY_FILTER = true
const VISIBLE_SUBCATEGORY_SLUGS = new Set(['explorer', 'producer'])
const selectedIndustrySubcategories = ref<string[]>([])
const selectedStates = ref<string[]>([])
const selectedCommodities = ref<string[]>([])
const selectedCashBands = ref<string[]>([])
const selectedEmployeeSizes = ref<string[]>([])
const hasActiveJobs = ref(false)
const selectedProjectStates = ref<string[]>([])
const selectedProjectStages = ref<string[]>([])
const bookmarkedOnly = ref(false)
const bookmarkSavingIds = ref<Set<number>>(new Set())

const companyDirectoryTotal = ref(0)
const projectDirectoryTotal = ref(0)
const companyResultTotal = ref<number | null>(null)
const projectResultTotal = ref<number | null>(null)
const companyItems = ref<CompanyListItem[]>([])
const projectItems = ref<ProjectListItem[]>([])
const companyIndustryOptions = ref<CompanyIndustryOption[]>([])
const total = ref(0)
const page = ref(1)

const defaultSortForMode = (mode: SearchMode): string => (mode === 'project' ? 'name_asc' : 'market_cap_desc')

const parseMode = (value: unknown): SearchMode => {
  const raw = Array.isArray(value) ? value[0] : value
  return raw === 'project' ? 'project' : 'company'
}

const parseQueryArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean)
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
  }

  return []
}

const parseQueryString = (value: unknown): string => {
  if (Array.isArray(value)) {
    return typeof value[0] === 'string' ? value[0] : ''
  }

  return typeof value === 'string' ? value : ''
}

const parseQueryPage = (value: unknown): number => {
  const rawValue = Array.isArray(value) ? value[0] : value
  const parsed = Number(rawValue)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1
}

const pageTitle = computed(() =>
  bookmarkedOnly.value
    ? 'Bookmarked Companies'
    : (activeMode.value === 'project' ? 'Search Projects' : 'Search Companies')
)
const pageSubtitle = computed(() =>
  bookmarkedOnly.value
    ? 'Review the companies you have bookmarked from the mining directory.'
    : (
  activeMode.value === 'project'
    ? 'Discover mining projects across Australia, then filter by stage, location, and commodities.'
    : 'Explore mining companies, compare market position and head office footprint, and open full profiles with project-level detail.'
    )
)
const searchPlaceholder = computed(() =>
  activeMode.value === 'project'
    ? 'Search projects, companies, commodities...'
    : 'Search companies, summaries, head offices...'
)
const sortOptions = computed(() => (activeMode.value === 'project' ? PROJECT_SORT_OPTIONS : COMPANY_SORT_OPTIONS))
const stateOptions = computed(() => JOB_STATE_FILTERS)
const displayDirectoryTotal = computed(() =>
  activeMode.value === 'project'
    ? (projectResultTotal.value ?? projectDirectoryTotal.value)
    : (companyResultTotal.value ?? companyDirectoryTotal.value)
)
const totalPages = computed(() => Math.max(1, Math.ceil(total.value / PER_PAGE)))
const resultsSummary = computed(() => {
  const singular = activeMode.value === 'project' ? 'project' : 'company'
  const plural = activeMode.value === 'project' ? 'projects' : 'companies'

  if (total.value === 0) {
    return `Showing 0 of 0 ${plural}`
  }

  const start = (page.value - 1) * PER_PAGE + 1
  const end = Math.min(start + (activeMode.value === 'project' ? projectItems.value.length : companyItems.value.length) - 1, total.value)

  return `Showing ${start.toLocaleString()}–${end.toLocaleString()} of ${total.value.toLocaleString()} ${total.value === 1 ? singular : plural}`
})
const activeFilterCount = computed(() => {
  if (bookmarkedOnly.value) {
    return 0
  }

  let count = 0
  if (searchQuery.value.trim()) count += 1
  count += selectedIndustries.value.length
  if (activeMode.value === 'company') {
    count += selectedIndustrySubcategories.value.length
  }
  count += selectedCommodities.value.length

  if (activeMode.value === 'project') {
    count += selectedProjectStages.value.length
    count += selectedProjectStates.value.length
    return count
  }

  count += selectedStates.value.length
  count += selectedCashBands.value.length
  count += selectedEmployeeSizes.value.length
  if (hasActiveJobs.value) count += 1
  return count
})
const mappableCompanies = computed(() =>
  companyItems.value.filter((company) => company.office_address && company.office_address.trim() !== '')
)
const activeCommodityOptions = computed(() => {
  const sourceItems = activeMode.value === 'project' ? projectItems.value : companyItems.value
  const set = new Set<string>(COMMODITIES)

  sourceItems.forEach((item) => {
    const commodities = Array.isArray(item.commodities) ? item.commodities : []
    commodities.forEach((commodity) => {
      const trimmed = String(commodity || '').trim()
      if (trimmed) {
        set.add(trimmed)
      }
    })
  })

  return Array.from(set).sort((a, b) => a.localeCompare(b))
})
const companySubcategoryOptions = computed<CompanyIndustrySubcategory[]>(() => {
  const selectedIndustrySet = new Set(selectedIndustries.value.map((industry) => industry.toLowerCase()))
  const relevantIndustries = selectedIndustrySet.size
    ? companyIndustryOptions.value.filter((industry) => selectedIndustrySet.has(industry.slug.toLowerCase()))
    : companyIndustryOptions.value

  const optionsBySlug = new Map<string, CompanyIndustrySubcategory>()
  relevantIndustries.forEach((industry) => {
    industry.subcategories.forEach((subcategory) => {
      const key = subcategory.slug || subcategory.name.trim().toLowerCase()
      if (!VISIBLE_SUBCATEGORY_SLUGS.has(key)) return
      if (!optionsBySlug.has(key)) {
        optionsBySlug.set(key, subcategory)
      }
    })
  })

  return Array.from(optionsBySlug.values()).sort((a, b) => a.name.localeCompare(b.name))
})
const filteredCommodityOptions = computed(() => {
  const needle = commoditySearchQuery.value.trim().toLowerCase()
  if (!needle) {
    return []
  }

  return activeCommodityOptions.value.filter(
    (commodity) =>
      !selectedCommodities.value.includes(commodity) &&
      commodity.toLowerCase().includes(needle)
  )
})
const activeCompanySortMetric = computed<{ key: 'cash_in_bank' | 'cash_runway'; label: string } | null>(() => {
  if (activeMode.value !== 'company') return null
  if (sort.value.startsWith('cash_in_bank')) return { key: 'cash_in_bank', label: 'Cash in bank' }
  if (sort.value.startsWith('cash_runway')) return { key: 'cash_runway', label: 'Cash runway' }
  return null
})

const formatMarketCapValue = (value: number | string | null): string => {
  if (value === null || Number.isNaN(value)) return '—'
  return formatMarketCap(value)
}

const parseNumber = (value: number | string | null): number | null => {
  if (value === null || value === '') return null
  const parsed = typeof value === 'number' ? value : Number(String(value).replace(/[$,]/g, ''))
  return Number.isFinite(parsed) ? parsed : null
}

const formatAudCurrency = (value: number | string | null): string => {
  const amount = parseNumber(value)
  if (amount === null) return '—'
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(amount)
}

const formatCashRunway = (value: number | string | null): string => {
  const amount = parseNumber(value)
  if (amount === null) return '—'
  return `${amount} ${amount === 1 ? 'quarter' : 'quarters'}`
}

const formatActiveCompanySortValue = (company: CompanyListItem): string => {
  if (activeCompanySortMetric.value?.key === 'cash_in_bank') {
    return formatAudCurrency(company.cash_in_bank)
  }

  if (activeCompanySortMetric.value?.key === 'cash_runway') {
    return formatCashRunway(company.cash_runway)
  }

  return ''
}

const visibleCommodities = (commodities: string[]): string[] => commodities.slice(0, 3)
const stripProtocol = (website: string): string => website.replace(/^https?:\/\//i, '')
const normalizeWebsite = (website: string): string =>
  /^https?:\/\//i.test(website) ? website : `https://${website}`
const getCompanyResultId = (company: CompanyListItem): string | number => company.id ?? company.slug ?? company.name

const toggleCommodity = (commodity: string) => {
  selectedCommodities.value = selectedCommodities.value.includes(commodity)
    ? selectedCommodities.value.filter((item) => item !== commodity)
    : [...selectedCommodities.value, commodity]

  commoditySearchQuery.value = ''
}

const toggleIndustry = (industry: string) => {
  selectedIndustries.value = selectedIndustries.value.includes(industry)
    ? selectedIndustries.value.filter((item) => item !== industry)
    : [...selectedIndustries.value, industry]

  pruneSelectedIndustrySubcategories()
}

const pruneSelectedIndustrySubcategories = () => {
  const available = new Set(companySubcategoryOptions.value.map((subcategory) => subcategory.slug))
  selectedIndustrySubcategories.value = selectedIndustrySubcategories.value.filter((subcategory) =>
    available.has(subcategory)
  )
}

const toggleIndustrySubcategory = (subcategory: string) => {
  selectedIndustrySubcategories.value = selectedIndustrySubcategories.value.includes(subcategory)
    ? selectedIndustrySubcategories.value.filter((item) => item !== subcategory)
    : [...selectedIndustrySubcategories.value, subcategory]
}

const toggleEmployeeSize = (size: string) => {
  selectedEmployeeSizes.value = selectedEmployeeSizes.value.includes(size)
    ? selectedEmployeeSizes.value.filter((item) => item !== size)
    : [...selectedEmployeeSizes.value, size]
}

const toggleCashBand = (band: string) => {
  selectedCashBands.value = selectedCashBands.value.includes(band)
    ? selectedCashBands.value.filter((item) => item !== band)
    : [...selectedCashBands.value, band]
}

const toggleState = (state: string) => {
  selectedStates.value = selectedStates.value.includes(state)
    ? selectedStates.value.filter((item) => item !== state)
    : [...selectedStates.value, state]
}

const toggleProjectState = (state: string) => {
  selectedProjectStates.value = selectedProjectStates.value.includes(state)
    ? selectedProjectStates.value.filter((item) => item !== state)
    : [...selectedProjectStates.value, state]
}

const toggleProjectStage = (stage: string) => {
  selectedProjectStages.value = selectedProjectStages.value.includes(stage)
    ? selectedProjectStages.value.filter((item) => item !== stage)
    : [...selectedProjectStages.value, stage]
}

const clearCompanyFilters = () => {
  commoditySearchQuery.value = ''
  selectedCommodities.value = []
  selectedIndustrySubcategories.value = []
  selectedStates.value = []
  selectedCashBands.value = []
  selectedEmployeeSizes.value = []
  hasActiveJobs.value = false
}

const clearProjectFilters = () => {
  commoditySearchQuery.value = ''
  selectedCommodities.value = []
  selectedProjectStates.value = []
  selectedProjectStages.value = []
}

const clearFilters = () => {
  searchQuery.value = ''
  sort.value = defaultSortForMode(activeMode.value)
  selectedIndustries.value = []
  bookmarkedOnly.value = false

  if (activeMode.value === 'project') {
    clearProjectFilters()
  } else {
    clearCompanyFilters()
  }

  queueSearch()
}

const setMode = (mode: SearchMode) => {
  if (activeMode.value === mode) return

  activeMode.value = mode
  bookmarkedOnly.value = false
  commoditySearchQuery.value = ''
  sort.value = defaultSortForMode(mode)
  page.value = 1
  selectedResultId.value = null
  total.value = mode === 'project'
    ? (projectResultTotal.value ?? projectDirectoryTotal.value)
    : (companyResultTotal.value ?? companyDirectoryTotal.value)

  if (mode === 'project') {
    clearCompanyFilters()
    companyItems.value = []
  } else {
    clearProjectFilters()
    projectItems.value = []
  }

  queueSearch()
}

const setBookmarkedOnly = (enabled: boolean) => {
  bookmarkedOnly.value = enabled
  activeMode.value = 'company'
  searchQuery.value = ''
  sort.value = defaultSortForMode('company')
  selectedIndustries.value = []
  clearCompanyFilters()
  clearProjectFilters()
  projectItems.value = []
  page.value = 1
  selectedResultId.value = null
  queueSearch()
}

const handleBookmarkedOnlyChange = (event: Event) => {
  setBookmarkedOnly(Boolean((event.target as HTMLInputElement | null)?.checked))
}

const syncStateFromRoute = () => {
  isSyncingFromRoute.value = true

  const nextBookmarkedOnly = parseQueryString(route.query.bookmarked ?? route.query.bookmarked_only) === '1'
  const nextMode = nextBookmarkedOnly ? 'company' : parseMode(route.query.mode)
  activeMode.value = nextMode
  bookmarkedOnly.value = nextBookmarkedOnly
  searchQuery.value = nextBookmarkedOnly ? '' : parseQueryString(route.query.q)
  sort.value = parseQueryString(route.query.sort) || defaultSortForMode(nextMode)
  selectedIndustries.value = nextBookmarkedOnly ? [] : parseQueryArray(route.query.industries)
  selectedIndustrySubcategories.value =
    nextBookmarkedOnly || !SHOW_SUBCATEGORY_FILTER ? [] : parseQueryArray(route.query.industry_subcategories)
  selectedStates.value = nextBookmarkedOnly ? [] : parseQueryArray(route.query.states ?? route.query.state)
  selectedCashBands.value = nextBookmarkedOnly ? [] : parseQueryArray(route.query.cash)
  selectedCommodities.value = nextBookmarkedOnly ? [] : parseQueryArray(route.query.commodities)
  selectedEmployeeSizes.value = nextBookmarkedOnly ? [] : parseQueryArray(route.query.employee_sizes)
  hasActiveJobs.value = nextBookmarkedOnly ? false : parseQueryString(route.query.active_jobs) === '1'
  selectedProjectStages.value = nextBookmarkedOnly ? [] : parseQueryArray(route.query.project_stages)
  selectedProjectStates.value = nextBookmarkedOnly ? [] : parseQueryArray(route.query.project_states)
  page.value = parseQueryPage(route.query.page)

  isSyncingFromRoute.value = false
}

const buildRouteQuery = (nextPage = 1): Record<string, string | string[]> => {
  const query: Record<string, string | string[]> = {}

  if (bookmarkedOnly.value) {
    query.bookmarked = '1'
    if (nextPage > 1) {
      query.page = String(nextPage)
    }
    return query
  }

  if (activeMode.value === 'project') {
    query.mode = 'project'
  }
  if (searchQuery.value.trim()) {
    query.q = searchQuery.value.trim()
  }
  if (sort.value !== defaultSortForMode(activeMode.value)) {
    query.sort = sort.value
  }
  if (selectedIndustries.value.length) {
    query.industries = [...selectedIndustries.value]
  }

  if (activeMode.value === 'project') {
    if (selectedCommodities.value.length) {
      query.commodities = [...selectedCommodities.value]
    }
    if (selectedProjectStages.value.length) {
      query.project_stages = [...selectedProjectStages.value]
    }
    if (selectedProjectStates.value.length) {
      query.project_states = [...selectedProjectStates.value]
    }
  } else {
    if (selectedCommodities.value.length) {
      query.commodities = [...selectedCommodities.value]
    }
    if (selectedIndustrySubcategories.value.length) {
      query.industry_subcategories = [...selectedIndustrySubcategories.value]
    }
    if (selectedStates.value.length) {
      query.states = [...selectedStates.value]
    }
    if (hasActiveJobs.value) {
      query.active_jobs = '1'
    }
    if (selectedCashBands.value.length) {
      query.cash = [...selectedCashBands.value]
    }
    if (selectedEmployeeSizes.value.length) {
      query.employee_sizes = [...selectedEmployeeSizes.value]
    }
  }

  if (nextPage > 1) {
    query.page = String(nextPage)
  }

  return query
}

const syncRouteQuery = (nextPage = 1) => {
  const nextQuery = buildRouteQuery(nextPage)
  const currentQuery = JSON.stringify(route.query)
  const targetQuery = JSON.stringify(nextQuery)

  if (currentQuery === targetQuery) {
    return
  }

  lastLocallySyncedQuery.value = targetQuery
  router.replace({
    name: 'ContractorCompanySearch',
    query: nextQuery,
  })
}

const buildParams = (nextPage: number): Record<string, unknown> => {
  const params: Record<string, unknown> = {
    mode: activeMode.value,
    page: nextPage,
    per_page: PER_PAGE,
    sort: sort.value,
  }

  if (bookmarkedOnly.value) {
    return {
      mode: 'company',
      page: nextPage,
      per_page: PER_PAGE,
      sort: defaultSortForMode('company'),
      bookmarked_only: true,
    }
  }

  if (searchQuery.value.trim() !== '') {
    params.q = searchQuery.value.trim()
  }
  if (selectedIndustries.value.length) {
    params.industries = selectedIndustries.value
  }
  if (selectedCommodities.value.length) {
    params.commodities = selectedCommodities.value
  }
  if (activeMode.value === 'company' && selectedIndustrySubcategories.value.length) {
    params.industry_subcategories = selectedIndustrySubcategories.value
  }

  if (activeMode.value === 'project') {
    if (selectedProjectStages.value.length) {
      params.project_stages = selectedProjectStages.value
    }
    if (selectedProjectStates.value.length) {
      params.project_states = selectedProjectStates.value
    }
    return params
  }

  if (selectedStates.value.length) {
    params.states = selectedStates.value
  }
  if (hasActiveJobs.value) {
    params.has_active_jobs = true
  }
  if (selectedCashBands.value.length) {
    params.cash_in_bank_bands = selectedCashBands.value
  }
  if (selectedEmployeeSizes.value.length) {
    params.employee_sizes = selectedEmployeeSizes.value
  }

  return params
}

const handleMarkerClick = (id: string | number | null) => {
  selectedResultId.value = id
}

const setBookmarkSaving = (companyId: number, saving: boolean) => {
  const next = new Set(bookmarkSavingIds.value)
  if (saving) {
    next.add(companyId)
  } else {
    next.delete(companyId)
  }
  bookmarkSavingIds.value = next
}

const updateCompanyBookmarkState = (companyId: number, isBookmarked: boolean) => {
  companyItems.value = companyItems.value.map((company) =>
    company.id === companyId
      ? { ...company, is_bookmarked: isBookmarked }
      : company
  )
}

const toggleCompanyBookmark = async (company: CompanyListItem) => {
  if (!company.id || bookmarkSavingIds.value.has(company.id)) return

  const companyId = company.id
  const previousItems = companyItems.value
  const previousTotal = total.value
  const previousCompanyResultTotal = companyResultTotal.value
  const nextBookmarkedState = !company.is_bookmarked
  setBookmarkSaving(companyId, true)

  if (nextBookmarkedState) {
    updateCompanyBookmarkState(companyId, true)
  } else if (bookmarkedOnly.value) {
    companyItems.value = companyItems.value.filter((item) => item.id !== companyId)
    total.value = Math.max(0, total.value - 1)
    companyResultTotal.value = Math.max(0, (companyResultTotal.value ?? total.value) - 1)
  } else {
    updateCompanyBookmarkState(companyId, false)
  }

  try {
    if (nextBookmarkedState) {
      await bookmarkContractorCompany(companyId)
      return
    }

    await removeContractorCompanyBookmark(companyId)
  } catch (error) {
    console.error('Failed to update company bookmark', error)
    companyItems.value = previousItems
    total.value = previousTotal
    companyResultTotal.value = previousCompanyResultTotal
  } finally {
    setBookmarkSaving(companyId, false)
  }
}

const loadDirectoryCounts = async () => {
  try {
    const [{ data: companyData }, { data: projectData }] = await Promise.all([
      getContractorMiningCompanies({
        mode: 'company',
        page: 1,
        per_page: 1,
        sort: defaultSortForMode('company'),
      }),
      getContractorMiningCompanies({
        mode: 'project',
        page: 1,
        per_page: 1,
        sort: defaultSortForMode('project'),
      }),
    ])

    companyDirectoryTotal.value = Number(companyData?.pagination?.total ?? 0)
    projectDirectoryTotal.value = Number(projectData?.pagination?.total ?? 0)

    if (companyResultTotal.value === null) {
      companyResultTotal.value = companyDirectoryTotal.value
    }
    if (projectResultTotal.value === null) {
      projectResultTotal.value = projectDirectoryTotal.value
    }
  } catch (error) {
    companyDirectoryTotal.value = 0
    projectDirectoryTotal.value = 0
  }
}

const runSearch = async (nextPage = 1) => {
  if (!isAllowed.value && hasBootstrapped.value) return

  isLoading.value = true
  selectedResultId.value = null
  syncRouteQuery(nextPage)

  try {
    const { data } = await getContractorMiningCompanies(buildParams(nextPage))
    isAllowed.value = true
    const nextTotal = Number(data?.pagination?.total ?? 0)

    if (activeMode.value === 'company') {
      companyItems.value = Array.isArray(data?.items) ? data.items : []
      companyResultTotal.value = nextTotal
      projectItems.value = []
    } else {
      projectItems.value = Array.isArray(data?.items) ? data.items : []
      projectResultTotal.value = nextTotal
      companyItems.value = []
    }

    total.value = nextTotal
    page.value = Number(data?.pagination?.page ?? nextPage)
  } catch (error: any) {
    if (error?.response?.status === 403) {
      isAllowed.value = false
      companyItems.value = []
      projectItems.value = []
      total.value = 0
      page.value = 1
      companyResultTotal.value = null
      projectResultTotal.value = null
      return
    }

    companyItems.value = []
    projectItems.value = []
    total.value = 0
    page.value = 1
  } finally {
    hasBootstrapped.value = true
    isAccessLoading.value = false
    isLoading.value = false
  }
}

let searchTimer: ReturnType<typeof setTimeout> | null = null
const queueSearch = () => {
  console.log('running queue search')
  if (isSyncingFromRoute.value) return
  if (!isAllowed.value && hasBootstrapped.value) return
  if (searchTimer) clearTimeout(searchTimer)

  searchTimer = setTimeout(() => {
    runSearch(1)
  }, 350)
}

watch([activeMode, searchQuery, sort], () => {
  queueSearch()
})

watch(
  [
    () => selectedIndustries.value.slice(),
    () => selectedIndustrySubcategories.value.slice(),
    () => selectedStates.value.slice(),
    () => selectedCashBands.value.slice(),
    () => selectedCommodities.value.slice(),
    () => selectedEmployeeSizes.value.slice(),
    hasActiveJobs,
    () => selectedProjectStages.value.slice(),
    () => selectedProjectStates.value.slice(),
  ],
  () => {
    queueSearch()
  }
)

onMounted(async () => {
  getCompanyIndustryOptions()
    .then(({ data }) => {
      companyIndustryOptions.value = Array.isArray(data?.data) ? data.data : []
      pruneSelectedIndustrySubcategories()
    })
    .catch((error) => {
      console.error('Failed to fetch company industry options', error)
    })
  syncStateFromRoute()
  await runSearch(page.value)
  if (isAllowed.value) {
    await loadDirectoryCounts()
  }
})

onBeforeUnmount(() => {
  if (searchTimer) {
    clearTimeout(searchTimer)
  }
})

watch(
  () => route.query,
  (nextQuery, previousQuery) => {
    if (JSON.stringify(nextQuery) === JSON.stringify(previousQuery)) {
      return
    }

    const serializedNextQuery = JSON.stringify(nextQuery)
    if (lastLocallySyncedQuery.value === serializedNextQuery) {
      lastLocallySyncedQuery.value = null
      return
    }

    syncStateFromRoute()

    if (!hasBootstrapped.value) {
      return
    }

    if (searchTimer) {
      clearTimeout(searchTimer)
      searchTimer = null
    }

    runSearch(page.value)
  }
)
</script>

<style scoped>
.company-list-enter-active,
.company-list-leave-active {
  transition: all 220ms ease;
}

.company-list-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.company-list-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
